import { apiClient } from "./apiClient";

/**
 * Audit-types whose `/audits/{id}/data-bundle/` endpoint produces a
 * meaningful zip (Excel + map PDF). Backend rejects the rest with a
 * 400 — we mirror the gate on the client so the trigger UI can be
 * disabled rather than firing a request we know will fail.
 */
export const BUNDLE_SUPPORTED_AUDIT_TYPES: ReadonlyArray<number> = [1, 2, 3, 5];

export const isBundleSupported = (auditTypeId: number | null | undefined): boolean =>
  auditTypeId != null && BUNDLE_SUPPORTED_AUDIT_TYPES.includes(auditTypeId);

interface DownloadOptions {
  /** Used to default the filename when the backend doesn't send one. */
  reference?: string | null;
}

/**
 * Calls `/audits/{id}/data-bundle/` and streams the zip to the user's
 * disk. Resolves once the download has been triggered; the caller
 * decides how to surface success/error messages.
 */
export async function downloadAuditBundle(
  auditId: number,
  opts: DownloadOptions = {},
): Promise<void> {
  const res = await apiClient.get(`/audits/${auditId}/data-bundle/`, {
    responseType: "blob",
  });
  const disposition = (res.headers["content-disposition"] || "") as string;
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename =
    match?.[1] || `auditoria_${opts.reference || auditId}.zip`;
  const blob = new Blob([res.data], { type: "application/zip" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * Fetch the data-upload Excel for an audit as a Blob. The caller is
 * expected to bundle it with other artefacts (e.g. the client-side
 * map PNG) before serving it to the user. Returns both the blob and
 * the filename the backend suggested via Content-Disposition so the
 * naming stays consistent across export flows.
 */
export async function fetchAuditExcelBlob(
  auditId: number,
  opts: DownloadOptions = {},
): Promise<{ blob: Blob; filename: string }> {
  const res = await apiClient.get(`/audits/${auditId}/data-excel/`, {
    responseType: "blob",
  });
  const disposition = (res.headers["content-disposition"] || "") as string;
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match?.[1] || `${opts.reference || auditId}.xlsx`;
  const blob = new Blob([res.data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  return { blob, filename };
}
